import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, User, Eye, EyeOff } from 'lucide-react';
import { API_URL } from '../config/api';

// Production mode - use real API
const USE_MOCK_LOGIN = false;
const MOCK_USERNAME = 'admin';
const MOCK_PASSWORD = 'KirikkaleFar2024!';

const AdminLogin = () => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        // Use mock login for testing (set USE_MOCK_LOGIN to true to enable)
        if (USE_MOCK_LOGIN) {
            await new Promise(resolve => setTimeout(resolve, 500));
            
            if (username === MOCK_USERNAME && password === MOCK_PASSWORD) {
                const mockToken = 'mock_jwt_token_' + Date.now();
                localStorage.setItem('adminToken', mockToken);
                localStorage.setItem('adminUsername', username);
                console.log('✅ Mock login successful');
                navigate('/admin');
            } else {
                setError('Kullanıcı adı veya şifre hatalı');
            }
            setLoading(false);
            return;
        }

        // Real API login
        try {
            console.log('🔐 Login attempt to:', `${API_URL}/login`);
            console.log('Username:', username);
            
            // First check if API is reachable
            const healthCheck = await fetch(`${API_URL}/settings`, {
                method: 'GET',
                mode: 'cors',
                cache: 'no-store'
            });
            
            if (!healthCheck.ok) {
                throw new Error('API sunucusu yanıt vermiyor (Status: ' + healthCheck.status + ')');
            }
            
            const response = await fetch(`${API_URL}/login`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ username, password }),
                mode: 'cors',
                cache: 'no-store'
            });

            console.log('Response status:', response.status);
            
            // Check if response is HTML (error page)
            const contentType = response.headers.get('content-type');
            console.log('Content-Type:', contentType);
            
            if (contentType && contentType.includes('text/html')) {
                const htmlText = await response.text();
                throw new Error('API HTML yanıtı döndürdü. Lütfen hosting kontrol edin. Hata: ' + htmlText.substring(0, 200));
            }
            
            const data = await response.json();
            console.log('Response data:', data);

            if (response.ok && data.token) {
                localStorage.setItem('adminToken', data.token);
                localStorage.setItem('adminUsername', data.username);
                console.log('✅ Login successful');
                navigate('/admin');
            } else {
                setError(data.error || 'Kullanıcı adı veya şifre hatalı');
            }
        } catch (err) {
            console.error('Login error:', err);
            // Show user-friendly error message
            let errorMsg = 'Giriş yapılırken bir hata oluştu.';
            
            if (err.message.includes('Failed to fetch')) {
                errorMsg = 'API sunucusuna bağlanılamadı. Lütfen:\n1. İnternet bağlantınızı kontrol edin\n2. API adresinin doğru olduğundan emin olun\n3. Hosting firmasıyla iletişime geçin';
            } else if (err.message.includes('API sunucusu yanıt vermiyor')) {
                errorMsg = err.message;
            } else {
                errorMsg = err.message;
            }
            
            setError(errorMsg);
        }

        setLoading(false);
    };

    return (
        <div className="min-h-screen bg-gray-100 flex items-center justify-center py-12 px-4">
            <div className="max-w-md w-full">
                <div className="text-center mb-8">
                    <div className="bg-blue-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Lock className="text-white w-8 h-8" />
                    </div>
                    <h1 className="text-2xl font-bold text-gray-800">Yönetici Girişi</h1>
                    <p className="text-gray-600 mt-2">KIRIKKALE FAR Yönetim Paneli</p>
                </div>

                <div className="bg-white rounded-lg shadow-md p-8">
                    <form onSubmit={handleSubmit}>
                        <div className="mb-6">
                            <label className="block text-gray-700 font-medium mb-2">
                                Kullanıcı Adı
                            </label>
                            <div className="relative">
                                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                                <input
                                    type="text"
                                    value={username}
                                    onChange={(e) => setUsername(e.target.value)}
                                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                                    placeholder="Kullanıcı adınız"
                                    required
                                />
                            </div>
                        </div>

                        <div className="mb-6">
                            <label className="block text-gray-700 font-medium mb-2">
                                Şifre
                            </label>
                            <div className="relative">
                                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                                    placeholder="Şifreniz"
                                    required
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                                >
                                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                                </button>
                            </div>
                        </div>

                        {error && (
                            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                                {error}
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors disabled:bg-blue-400"
                        >
                            {loading ? 'Giriş yapılıyor...' : 'Giriş Yap'}
                        </button>
                    </form>
                </div>

                <div className="text-center mt-6">
                    <a href="/" className="text-blue-600 hover:underline">
                        ← Siteye geri dön
                    </a>
                </div>
            </div>
        </div>
    );
};

export default AdminLogin;
